import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { AudioTechnology, MonetizationType, PresentationType, VideoTechnology } from '@/@types/graphql-types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { BuyboxParam } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask } from '@/components/click-masking/click-masking-config';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import {
	observableOptions,
	type OfferVisibilityChangedPayload,
} from '@/helpers/composables/useOfferImpressionTracking';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { subscriptionPricesUS, subscriptionPricesUK } from '@/components/buybox-new/new-buybox-config';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxOfferNew',
  props: {
    offer: null,
    title: null
  },
  emits: ["clickOut", "visibilityChanged"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
			isBundle: boolean;
			isLeavingIconShown: boolean;
			evt: MouseEvent;
		}
	): void;
	(
		e: 'visibilityChanged',
		data: OfferVisibilityChangedPayload & {
			element: TitleOfferFragment & { iconUrl?: string; bundleId?: number; isLeavingIconShown: boolean };
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
	title: TitleDetailsNode;
}

const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');
const BuyBoxInfoModal = () => import('@/components/buybox-new/BuyBoxInfoModal.vue');

const { country, webLocale } = useLanguageStore();

const { tc } = useI18n();





const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage();

const isPlexProvider = computed(() => props.offer?.package.technicalName === 'plexplayer');

const openTrailerModal = () => {
	if (!isPlexProvider.value) return;
	ModalHelper.openModal(
		PlexPlayerModal,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
};

let displayBuyBoxInfoModal = ref<boolean>(false);

const toggleBuyBoxInfoModal = () => {
	displayBuyBoxInfoModal.value = !displayBuyBoxInfoModal.value;
};

const shouldMaskClicks = computed(() => shouldClickMask(props.offer?.package.packageId, country.value));

const offerUrl = computed(() => {
	if (isPlexProvider.value) return undefined;
	if (shouldMaskClicks.value) return props.offer?.standardWebURL?.split('?')[0];

	const url = new ClickoutUrl(props.offer).set('uct_country', country.value);

	if (isArbitrating.value) {
		return withArbitrageTracking(url, () => props.offer).toString();
	} else {
		url.set('uct_buybox', BuyboxParam.BuyBoxExp4Variant2);
	}

	return url.toString();
});

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer?.package.packageId, webLocale.value)
		? 'nofollow sponsored'
		: 'nofollow'
);

const referrerpolicy = computed(() => (shouldMaskClicks.value ? 'no-referrer' : undefined));

const onClickOut = (evt: MouseEvent) => {
	trackTagEvent(() => props.offer);
	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.offer?.iconUrl,
		isLeavingIconShown: false,
		evt,
	});
};

const onVisibilityChanged = (isVisible: boolean) => {
	emit('visibilityChanged', {
		isVisible,
		element: { ...props.offer, isLeavingIconShown: false },
	});
};

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const packageImg = computed(() => {
	let iconFilename = '';
	if (country.value === 'US') {
		iconFilename =
			subscriptionPricesUS.find(provider => {
				return provider.id === props.offer?.package.packageId;
			})?.provider_icon ?? '';
	} else if (country.value === 'GB') {
		iconFilename =
			subscriptionPricesUK.find(provider => {
				return provider.id === props.offer?.package.packageId;
			})?.provider_icon ?? '';
	}
	return iconFilename ? `https://www.justwatch.com/appassets/img/rectangular-provider-icons/${iconFilename}` : null;
});

const languageArrayToString = (languageArray: string[]) => {
	const languages = languageArray.map(language => {
		return {
			language,
			name: tc(`WEBAPP_LANGUAGE_${language.toUpperCase()}`),
		};
	});
	const filteredLanguages = languages.filter((language: { language: string; name: string }) => {
		return !language.name.startsWith('WEBAPP_LANGUAGE_');
	});
	return filteredLanguages
		.map((language: { language: string; name: string }) => {
			return language.name;
		})
		.join(', ');
};

const audioLanguages = computed(() => {
	if (!props.offer?.audioLanguages) return false;
	return languageArrayToString(props?.offer?.audioLanguages);
});

const retailPrice = computed(() => {
	if (props.offer?.retailPrice) {
		return props.offer?.retailPrice;
	} else {
		let provider;
		if (country.value === 'US') {
			provider = subscriptionPricesUS.find(provider => {
				return provider.id === props.offer?.package.packageId;
			});
		} else if (country.value === 'GB') {
			provider = subscriptionPricesUK.find(provider => {
				return provider.id === props.offer?.package.packageId;
			});
		}
		if (
			!provider ||
			!provider.price ||
			[MonetizationType.Ads, MonetizationType.Free].includes(props.offer?.monetizationType)
		)
			return null;
		return `${country.value === 'US' ? '$' : '£'}${provider.price} / month`;
	}
});

const hasfreeTrial = computed(() => {
	return !!freeTrialText.value && props.offer?.monetizationType === MonetizationType.Flatrate;
});

const freeTrialText = computed(() => {
	let provider;
	if (country.value === 'US') {
		provider = subscriptionPricesUS.find(provider => {
			return provider.id === props.offer?.package.packageId;
		});
	} else if (country.value === 'GB') {
		provider = subscriptionPricesUK.find(provider => {
			return provider.id === props.offer?.package.packageId;
		});
	}
	return provider?.free_trial;
});

const offerLabel = (monetizationType: MonetizationType) => {
	const monetizationTypeMapping = {
		ADS: 'Free',
		BUY: 'Buy',
		CINEMA: 'Cinema',
		FAST: 'Free TV',
		FLATRATE: 'Subscription',
		FLATRATE_AND_BUY: 'Buy',
		FREE: 'Free',
		RENT: 'Rent',
	};
	return monetizationTypeMapping[monetizationType];
};

const presentationTypeLabel = computed(() => {
	switch (props.offer?.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const offerId = computed(() => props.offer?.bundleId?.toString() ?? props.offer?.id);

const ageRating = computed(() => {
	return props.title.content.ageCertification;
});

const dolbyTechnology = computed(() => {
	return (
		props.offer?.videoTechnology.includes(VideoTechnology.DolbyVision) ||
		props.offer?.audioTechnology.includes(AudioTechnology.Dolby_5Point_1) ||
		props.offer?.audioTechnology.includes(AudioTechnology.DolbyAtmos)
	);
});

const runtimeMins = computed(() => {
	return `${props.title.content.runtime}min`;
});

const highlightCta = computed(() => {
	return props.offer?.monetizationType === MonetizationType.Flatrate;
});

return { __sfc: true,PlexPlayerModal, BuyBoxInfoModal, country, webLocale, tc, props, emit, isArbitrating, withArbitrageTracking, trackTagEvent, isPlexProvider, openTrailerModal, displayBuyBoxInfoModal, toggleBuyBoxInfoModal, shouldMaskClicks, offerUrl, isSponsoredOffer, referrerpolicy, onClickOut, onVisibilityChanged, observeVisibilityOptions, packageImg, languageArrayToString, audioLanguages, retailPrice, hasfreeTrial, freeTrialText, offerLabel, presentationTypeLabel, offerId, ageRating, dolbyTechnology, runtimeMins, highlightCta }
}

})